import { useEffect, useRef } from "react";

// Make the browser Back button close in-page overlays (modals, drawers, side panels) before it
// leaves the current nav tab. Overlays opt in with the useBackClose hook: opening pushes a history
// entry + registers a close handler; Back pops the top handler instead of switching tabs. App
// installs the single popstate handler and asks navHandleBack() first.
let stack = [];            // [{ close, viaBack }] — top of stack = most recently opened
let skipNextPop = false;   // set when a UI-close unwinds its own history entry
let getPage = () => null;

export function configureNavStack(pageGetter) { getPage = pageGetter; }

// App's popstate handler calls this FIRST. Returns true if it consumed the Back — either by
// closing the top overlay, or by absorbing the history.back() a UI-close fired — in which case
// the caller must NOT change the tab.
export function navHandleBack() {
  if (skipNextPop) { skipNextPop = false; return true; }
  const top = stack.pop();
  if (top) { top.viaBack = true; try { top.close(); } catch (e) {} return true; }
  return false;
}

// Tab switched via the nav menu — overlays from the old page are gone; drop stale handlers.
export function navReset() { stack = []; }

let seq = 0;               // tags each overlay's history entry so navClose only unwinds ITS OWN entry
function navOpen(close) {
  const entry = { close, viaBack: false, id: ++seq };
  stack.push(entry);
  try { window.history.pushState({ overlay: true, page: getPage(), id: entry.id }, ""); } catch (e) {}
  return entry;
}

// Overlay closed via the UI (its state went falsy). If it wasn't already closed by Back, unwind
// the history entry it pushed so a later Back press isn't wasted.
//
// Deferred + guarded: an overlay also "closes" when it UNMOUNTS because the tab changed (e.g. a
// nav tap while a message thread / bottom sheet is open). In that same commit App pushes the new
// {page} entry, so a synchronous history.back() here undid the tab change — the first nav tap from
// an open sheet did nothing on the phone. Waiting a tick and checking that the current entry is
// still ours makes the tab change win; a stale overlay entry left behind costs at most one extra
// Back press later.
function navClose(entry) {
  const i = stack.indexOf(entry);
  if (i === -1) return;            // already removed by navHandleBack (closed via Back)
  stack.splice(i, 1);
  if (entry.viaBack) return;
  setTimeout(() => {
    const st = window.history.state;
    if (st && st.overlay && st.id === entry.id) {
      skipNextPop = true;
      try { window.history.back(); } catch (e) { skipNextPop = false; }
    }
  }, 0);
}

// Opt an overlay into Back-to-close. `isOpen` is the overlay's open state; `close` resets it.
// No call-site changes needed — this observes the state and keeps history in sync either way.
export function useBackClose(isOpen, close) {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!isOpen) return undefined;
    const entry = navOpen(() => closeRef.current());
    return () => navClose(entry);
  }, [isOpen]);
}
